// Tipos baseados nos DTOs do backend

// Usuário autenticado — perfil sempre presente (vem do JWT)
export interface TUsuario {
  id: number;
  nome: string;
  login: string;
  email: string;
  fone?: string;
  endereco?: string;
  perfil: string;
  ativo: boolean;
  dataCriacao: string;
  ultimoLogin?: string;
  permissoes: string[];
  /** Papel do usuário — o backend já devolvia (UsuarioResponse.PapelId); o tipo é
   *  que não expunha. A matriz de Papéis usa pra saber qual é o PRÓPRIO papel de
   *  quem está logado (não-ADMIN não pode editar o dele). */
  papelId?: number | null;
}

// Usuário no gerenciamento de usuários — perfil pode ser omitido em listagens antigas
export interface Usuario extends Omit<TUsuario, 'perfil'> {
  perfil?: string;
  /**
   * Papel (cargo) — fonte ÚNICA das permissões desde o RBAC puro.
   * `null` = sem papel ⇒ sem acesso (a lista mostra o badge "Sem acesso").
   */
  papelId?: number | null;
  papelNome?: string | null;
}

// Filtros para busca de usuários
export interface UsuariosFilters {
  search?: string;
  perfil?: string;
  ativo?: boolean;
  page?: number;
  pageSize?: number;
}

// Response da API
export interface UsuariosResponse {
  success: boolean;
  data: Usuario[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  message: string;
}

// Para criação de usuário
export interface CriarUsuarioRequest {
  nome: string;
  login: string;
  email: string;
  senha: string;
  fone?: string;
  endereco?: string;
  perfil: string;
  ativo: boolean;
}

// Para atualização de usuário
export interface AtualizarUsuarioRequest {
  nome?: string;
  login?: string;
  email?: string;
  fone?: string;
  endereco?: string;
  perfil?: string;
}

// Para alterar status
export interface AlterarStatusRequest {
  ativo: boolean;
}

// Para redefinir senha
export interface RedefinirSenhaRequest {
  novaSenha: string;
}

// Para alterar própria senha
export interface AlterarSenhaRequest {
  senhaAtual: string;
  novaSenha: string;
}

// Estatísticas
export interface EstatisticasUsuarios {
  totalUsuarios: number;
  usuariosAtivos: number;
  usuariosInativos: number;
  adminCount: number;
  userCount: number;
  loginsHoje: number;
  loginsUltimos7Dias: number;
  ultimoLogin?: string;
}

// Response de estatísticas
export interface EstatisticasResponse {
  success: boolean;
  data: EstatisticasUsuarios;
  message: string;
}

// Response simples
export interface SimpleResponse {
  success: boolean;
  message: string;
}

// Permissões do sistema — deve espelhar backend/Models/Postgres/Permissoes.cs
/**
 * Status de cada recurso/permissão:
 * - `available`: funciona normalmente, aparece em Sidebar/Home/Editor.
 * - `beta`: visível mas com badge — admin libera ciente que pode ter bugs.
 * - `disabled`: invisível em todo lugar (Sidebar, Home, Editor, rotas
 *   bloqueadas no ProtectedRoute). Dev controla via código — nem ADMIN
 *   consegue liberar. Use pra telas em desenvolvimento que não devem ser
 *   liberadas em produção ainda.
 */
export type PermissaoStatus = 'available' | 'beta' | 'disabled';

interface PermissaoMeta {
  label: string;
  status: PermissaoStatus;
}

export const PERMISSOES = {
  dashboard:           { label: 'Dashboard',                       status: 'disabled' as PermissaoStatus },
  pedidos:             { label: 'Pedidos',                         status: 'available' as PermissaoStatus },
  nfe_terceiros:       { label: 'NF-e Terceiros',                  status: 'available' as PermissaoStatus },
  cte_terceiros:       { label: 'CT-e Terceiros',                  status: 'available' as PermissaoStatus },
  conferencia_nfe:     { label: 'Conferência NFe',                 status: 'available' as PermissaoStatus },
  // Quem TRATA pendência (resolver/reabrir nota já resolvida), separado de quem
  // OPERA a doca (bipar/conferir — conferencia_nfe acima).
  conferencia_pendencia: { label: 'Conferência NFe — Tratar pendências', status: 'available' as PermissaoStatus },
  gerar_catalogo:      { label: 'Gerar Catálogo',                  status: 'available' as PermissaoStatus },
  catalogo_online:     { label: 'Catálogo Online',                 status: 'available' as PermissaoStatus },
  agrupamentos:        { label: 'Agrupamentos',                    status: 'available' as PermissaoStatus },
  gerenciar_usuarios:  { label: 'Gerenciar Usuários',              status: 'available' as PermissaoStatus },
  gerenciar_papeis:    { label: 'Gerenciar Papéis',                status: 'available' as PermissaoStatus },
  auditoria:           { label: 'Auditoria',                       status: 'available' as PermissaoStatus },
  explorador:          { label: 'Explorador de dados',             status: 'available' as PermissaoStatus },
  // Compras — granulares por sub-módulo (espelha Permissoes.cs)
  'compras.reposicao': { label: 'Compras — Reposição',             status: 'available' as PermissaoStatus },
  'compras.analise':   { label: 'Compras — Análise/Sugestão',      status: 'available' as PermissaoStatus },
  'compras.sugestoes': { label: 'Compras — Sugestões salvas',      status: 'available' as PermissaoStatus },
  'compras.cotacao':   { label: 'Compras — Cotação',               status: 'available' as PermissaoStatus },
  'compras.pedido':       { label: 'Compras — Pedidos',                  status: 'available' as PermissaoStatus },
  'compras.metas':        { label: 'Compras — Metas',                    status: 'available' as PermissaoStatus },
  'compras.fornecedores': { label: 'Compras — Gestão de Fornecedores',   status: 'available' as PermissaoStatus },
  'compras.marcas':       { label: 'Compras — Gestão de Marcas',         status: 'available' as PermissaoStatus },
  // Ações sensíveis (Fase B 2026-05-21) — granular por ação destrutiva/efetivadora
  'compras.pedido.excluir':         { label: 'Compras — Excluir pedido',                             status: 'available' as PermissaoStatus },
  'compras.sugestao.gerar_pedido':  { label: 'Compras — Gerar pedido a partir de sugestão (envia ao ERP)', status: 'available' as PermissaoStatus },
  'compras.cotacao.gerar_pedido':   { label: 'Compras — Gerar pedidos a partir de cotação (envia ao ERP)', status: 'available' as PermissaoStatus },
  'compras.cotacao.cancelar':       { label: 'Compras — Cancelar cotação',                           status: 'available' as PermissaoStatus },
  'compras.cotacao.revogar_token':  { label: 'Compras — Revogar token de fornecedor (link público)', status: 'available' as PermissaoStatus },
  'compras.meta.gravar':            { label: 'Compras — Criar / editar meta',                        status: 'available' as PermissaoStatus },
  'compras.meta.excluir':           { label: 'Compras — Excluir meta',                               status: 'available' as PermissaoStatus },
  'compras.lote':                   { label: 'Compras — Edição em lote (marcas, fornecedores, metas)', status: 'available' as PermissaoStatus },
} satisfies Record<string, PermissaoMeta>;

/**
 * True se a feature está liberada pra uso. Use pra esconder rotas em Sidebar,
 * filtrar cards na HomePage, bloquear acesso direto em ProtectedRoute, etc.
 * Disabled em código: nem ADMIN consegue acessar.
 */
export function isPermissaoDisponivel(chave: string): boolean {
  const meta = (PERMISSOES as Record<string, PermissaoMeta>)[chave];
  return meta != null && meta.status !== 'disabled';
}

/**
 * Agrupamento das permissões pra renderizar com toggles organizados por categoria
 * no PermissoesEditor. Cada categoria tem label + lista de chaves.
 * Chaves com status='disabled' são filtradas pelo Editor antes de renderizar.
 */
export const PERMISSOES_GRUPOS: Array<{ label: string; chaves: PermissaoChave[] }> = [
  {
    label: 'Operacional',
    chaves: ['dashboard', 'pedidos', 'explorador', 'nfe_terceiros', 'cte_terceiros', 'conferencia_nfe', 'conferencia_pendencia'],
  },
  {
    label: 'Catálogo',
    chaves: ['gerar_catalogo', 'catalogo_online', 'agrupamentos'],
  },
  {
    label: 'Compras',
    chaves: [
      'compras.reposicao',
      'compras.analise',
      'compras.sugestoes',
      'compras.cotacao',
      'compras.pedido',
      'compras.metas',
      'compras.fornecedores',
      'compras.marcas',
    ],
  },
  {
    // Ações sensíveis (Fase B 2026-05-21) — granular por ação destrutiva/efetivadora.
    // Cada chave é um ADD-ON sobre a chave da tela. Ex.: pra excluir pedido o user
    // precisa de `compras.pedido` (ver tela) + `compras.pedido.excluir` (ação).
    label: 'Compras — Ações sensíveis',
    chaves: [
      'compras.pedido.excluir',
      'compras.sugestao.gerar_pedido',
      'compras.cotacao.gerar_pedido',
      'compras.cotacao.cancelar',
      'compras.cotacao.revogar_token',
      'compras.meta.gravar',
      'compras.meta.excluir',
      'compras.lote',
    ],
  },
  {
    label: 'Sistema',
    chaves: ['gerenciar_usuarios', 'gerenciar_papeis', 'auditoria'],
  },
];

export type PermissaoChave = keyof typeof PERMISSOES;
